import {
  FOC_TOUR_LABEL_INITIAL_RESPONSE,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';
import {
  focInitialResponseDemoHref,
  preferredFocInitialResponseId,
  type FocInitialResponseIncident,
} from './focInitialResponseDemo';
import { INITIAL_RESPONSE_VIEW_PARAM } from 'utils/initialResponseView';

export const FOC_INITIAL_RESPONSE_TOUR_PARAM = 'focInitialResponseTour';

/** Hub entry; host resolves an unescalated incident and opens New ICS 201. */
export const FOC_INITIAL_RESPONSE_TOUR_HREF = `/hub?openTab=${CorporateViewTabs.Incidents}&${FOC_INITIAL_RESPONSE_TOUR_PARAM}=1`;

export type FocInitialResponseTourSurface = 'hub' | 'initialResponse';

export type FocInitialResponseTourStepId =
  | 'sectionIntro'
  | 'sec81'
  | 'ir001'
  | 'ir001a'
  | 'ir001b'
  | 'ir001c'
  | 'ir002'
  | 'ir002a'
  | 'ir002b'
  | 'ir003'
  | 'sec83'
  | 'usoa001'
  | 'usoa002'
  | 'usoa004'
  | 'usoa005'
  | 'usoa007'
  | 'usoa013'
  | 'usoa014'
  | 'usoa015'
  | 'usoa017'
  | 'usoa018'
  | 'ir004'
  | 'ir004a'
  | 'ir005'
  | 'ir005a'
  | 'ir005b'
  | 'sec84'
  | 'ussit001'
  | 'ussit002'
  | 'ussit005'
  | 'ussit010'
  | 'ussa002'
  | 'ussa005'
  | 'ir006'
  | 'focFeedback';

export type FocInitialResponseTourStepDef = {
  id: FocInitialResponseTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocInitialResponseTourSurface;
};

type RequirementInput = Omit<
  FocInitialResponseTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocInitialResponseTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 8.1–8.4 Initial Response / ICS 201 on the New forms workspace.
 */
export const FOC_INITIAL_RESPONSE_TOUR_STEPS: FocInitialResponseTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'initialResponse',
    tour: 'incident-ir-forms',
    title: '8.1–8.4 Initial Response / ICS 201',
    description:
      'Walk the Initial Response operating model in the New ICS 201 workspace — rapid capture of situation, objectives, actions, map, organization, resources, safety, and signature before Planning P.',
  },
  {
    id: 'sec81',
    surface: 'initialResponse',
    tour: 'ir-view-toggle-new',
    title: '8.1 Initial Response Operating Model',
    description:
      'ICS 201 initial response is the immediate phase after an incident exceeds normal operations. PRATUS keeps this workflow distinct from the recurring Planning P cycle. This tour uses the New ICS 201 experience.',
  },
  requirement({
    id: 'ir001',
    requirementId: 'FOC-IR-001',
    name: 'Rapid-Capture ICS 201 Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-card',
    description:
      'PRATUS provides a rapid-capture ICS 201 workspace for current situation, initial objectives, actions, map, resources, organization, safety concerns, roster, and documentation.',
  }),
  requirement({
    id: 'ir001a',
    requirementId: 'FOC-IR-001a',
    name: 'Persistent Operational Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-situation',
    description:
      'The initial response workspace presents current situation, objectives, actions, activity log, map, roster/organization, resources, safety, and a clear path toward Planning P without leaving the operational context.',
  }),
  requirement({
    id: 'ir001b',
    requirementId: 'FOC-IR-001b',
    name: 'Living Initial-Action Product',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-export',
    description:
      'PRATUS treats the ICS 201 as a living initial-action product: continuous update and rapid capture, with the ability to generate and export a source-faithful ICS 201.',
  }),
  requirement({
    id: 'ir001c',
    requirementId: 'FOC-IR-001c',
    name: 'Optional Integration Enrichment',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    description:
      'Authorized enrichment from approved sources is optional. Unavailable external data does not block creating, managing, or escalating an incident.',
  }),
  requirement({
    id: 'ir002',
    requirementId: 'FOC-IR-002',
    name: 'Initial Incident Map',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-map',
    description:
      'PRATUS provides an in-context initial-response map on the ICS 201 card. Compose layers here, then Take Snapshot for ICS-201 Export so Box 4 matches the framed view.',
  }),
  requirement({
    id: 'ir002a',
    requirementId: 'FOC-IR-002a',
    name: 'Geometry Synchronization',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-location',
    description:
      'Incident creation location, address, coordinates, and geometries synchronize with the initial-response map and incident record.',
  }),
  requirement({
    id: 'ir002b',
    requirementId: 'FOC-IR-002b',
    name: 'Rapid Map Features',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-go-primary-map',
    description:
      'Use Go to Primary Map to open the incident Map Layers workspace. There, authorized non-GIS users can place, move, edit, or remove common initial-response features such as ICP, staging, divisions, and points of interest. Features on synced layers appear in the ICS 201 sketch when you return and take a snapshot.',
  }),
  requirement({
    id: 'ir003',
    requirementId: 'FOC-IR-003',
    name: 'Initial Incident Signing and Auditability',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-prepared-by',
    description:
      'Initial signature defaults to the authenticated user, supports role/position selection and later IC updates, and records date/time. Initial response is not blocked solely because a formal IC seat is empty.',
  }),
  {
    id: 'sec83',
    surface: 'initialResponse',
    tour: 'incident-ir-content',
    title: '8.3 Initial Response Objectives and Actions',
    description:
      'Objectives and running actions live on the ICS 201 card so responders can prioritize and track tactical work during initial response.',
  },
  requirement({
    id: 'usoa001',
    requirementId: 'US-OA001',
    name: 'View Objectives and Independent Actions',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-objectives',
    description:
      'View the list of incident objectives and independent actions (actions not tied to a specific objective) in the ICS 201 objectives and actions box.',
  }),
  requirement({
    id: 'usoa002',
    requirementId: 'US-OA002',
    name: 'View Objective Type',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-objective-type',
    description:
      'Each objective shows its type (Operational or Managerial) as a badge so you can categorize objectives at a glance.',
  }),
  requirement({
    id: 'usoa004',
    requirementId: 'US-OA004',
    name: 'Search Objectives and Actions',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-oa-search',
    description:
      'Search objectives or actions by title or description to find relevant work quickly.',
  }),
  requirement({
    id: 'usoa005',
    requirementId: 'US-OA005',
    name: 'View Action Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-actions',
    description:
      'Action rows show description, status, and assignee so you have complete information about each tactical task.',
  }),
  requirement({
    id: 'usoa007',
    requirementId: 'US-OA007',
    name: 'Add Objective',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-add-objective',
    description:
      'Authorized editors can add new objectives to prioritize urgent strategic goals during initial response.',
  }),
  requirement({
    id: 'usoa013',
    requirementId: 'US-OA013',
    name: 'Add Child Action',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-add-action',
    description:
      'Add child actions that support an objective so tactical tasks map to strategic goals.',
  }),
  requirement({
    id: 'usoa014',
    requirementId: 'US-OA014',
    name: 'Add Independent Action',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    description:
      'Create independent actions not associated with a parent objective when tactical work does not fit the current hierarchy. Use Add Action without linking a parent objective.',
  }),
  requirement({
    id: 'usoa015',
    requirementId: 'US-OA015',
    name: 'View Action Assignee',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-action-assignee',
    description:
      'Each action shows its assignee so you know who is responsible for completing the work.',
  }),
  requirement({
    id: 'usoa017',
    requirementId: 'US-OA017',
    name: 'Save or Cancel Action Edits',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    description:
      'When editing an action, you can save or cancel so changes are only committed when you choose. This walkthrough does not mutate actions.',
  }),
  requirement({
    id: 'usoa018',
    requirementId: 'US-OA018',
    name: 'View Action Status',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-action-status',
    description:
      'Actions display status (Current, Planned, and related values) so you understand the timeline for each task.',
  }),
  requirement({
    id: 'ir004',
    requirementId: 'FOC-IR-004',
    name: 'Objective Priority and Sequencing',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-objectives',
    description:
      'Authorized users can create, revise, prioritize, categorize, and reorder initial incident objectives. Order communicates priorities in operational products.',
  }),
  requirement({
    id: 'ir004a',
    requirementId: 'FOC-IR-004a',
    name: 'Optional Objective Assistance',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    description:
      'Configurable, source-grounded objective recommendations may be offered. Users accept, modify, reorder, or reject them — suggestions never become objectives without authorized human action.',
  }),
  requirement({
    id: 'ir005',
    requirementId: 'FOC-IR-005',
    name: 'Timestamped Running Action Log',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-actions',
    description:
      'PRATUS provides a timestamped running action log for current and planned actions, observations, decisions, and operational updates during initial response.',
  }),
  requirement({
    id: 'ir005a',
    requirementId: 'FOC-IR-005a',
    name: 'Low-Friction Entry',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-add-action',
    description:
      'Capture an initial action without mandatory assignment or long-form fields when those details are not needed for the immediate record.',
  }),
  requirement({
    id: 'ir005b',
    requirementId: 'FOC-IR-005b',
    name: 'Action Continuity',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    description:
      'Action history and status persist as the incident transitions from ICS 201 into Planning P and recurring operational periods.',
  }),
  {
    id: 'sec84',
    surface: 'initialResponse',
    title: '8.4 Initial Response SITREP and Safety',
    description:
      'An incident can carry a formal Report / SITREP product in addition to the ICS 201 briefing. This section opens Reports, then returns to ICS 201 for safety.',
  },
  requirement({
    id: 'ussit001',
    requirementId: 'US-IR001',
    name: 'Access Incident SITREP Panel',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'reports-intro',
    description:
      'Access the incident Reports panel from Initial Response. Formal SITREP / report workflows live here — distinct from the ICS 201 Current Situation box.',
  }),
  requirement({
    id: 'ussit002',
    requirementId: 'US-IR002',
    name: 'View Current Incident SITREP',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'reports-tabs',
    description:
      'View SITREP and other report types for the incident so you stay aligned with the latest official status products.',
  }),
  requirement({
    id: 'ussit005',
    requirementId: 'US-IR005',
    name: 'Submit Incident SITREP for Approval',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'reports-create-draft',
    description:
      'Draft a Report / SITREP and submit it for Incident Commander or Approver review before it becomes the official record. This walkthrough does not submit.',
  }),
  requirement({
    id: 'ussit010',
    requirementId: 'US-IR010',
    name: 'Export Incident SITREP',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    description:
      'Export the incident Report / SITREP (for example as PDF) for external briefings or physical distribution. Export controls live in the Reports draft menu — this walkthrough does not export.',
  }),
  requirement({
    id: 'ussa002',
    requirementId: 'US-SA002',
    name: 'Edit ICS 201 Safety Analysis',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-safety',
    description:
      'View and edit Safety Analysis fields on the ICS 201 so immediate safety concerns are documented for initial response.',
  }),
  requirement({
    id: 'ussa005',
    requirementId: 'US-SA005',
    name: 'Export Safety Documents',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-export',
    description:
      'Export completed ICS-201 and related safety products (for example PDF) to distribute official safety plans to stakeholders and field personnel.',
  }),
  requirement({
    id: 'ir006',
    requirementId: 'FOC-IR-006',
    name: 'Initial Safety Visibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'initialResponse',
    tour: 'ics201-safety',
    description:
      'Active safety concerns, messages, and applicable safety products are presented in the initial-response workspace and can link to objectives, actions, map features, and later planning products.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_INITIAL_RESPONSE),
  },
];

export function focInitialResponseTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focInitialResponseHref(incidentId: string): string {
  return focInitialResponseDemoHref(incidentId);
}

/** Prefer FOC IR demo / unescalated live incident with New ICS 201 pinned. */
export function resolveFocInitialResponseHref(
  incidents: readonly FocInitialResponseIncident[] = [],
  currentId?: string | null
): string | null {
  const id = preferredFocInitialResponseId(incidents, currentId);
  return id ? focInitialResponseHref(id) : null;
}

export function focInitialResponseTourStepIds(): FocInitialResponseTourStepId[] {
  return FOC_INITIAL_RESPONSE_TOUR_STEPS.map((step) => step.id);
}

export function focInitialResponseTourStepTitles(): string[] {
  return FOC_INITIAL_RESPONSE_TOUR_STEPS.map((step) => step.title);
}

export function focInitialResponseTourPopoverHtml(
  step: FocInitialResponseTourStepDef
): string {
  const parts: string[] = [];
  if (step.disposition) {
    parts.push(
      `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>`
    );
  }
  parts.push(`<p>${escapeHtml(step.description)}</p>`);
  if (step.bullets?.length) {
    parts.push(
      `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    );
  }
  return parts.join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Re-export for hosts that need the view param name.
export { INITIAL_RESPONSE_VIEW_PARAM };
